import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";

const BUSINESS_TYPES = [
  { value: "restaurant", label: "Restaurante" },
  { value: "cafeteria", label: "Cafetería" },
  { value: "bar", label: "Bar" },
  { value: "pizzeria", label: "Pizzería" },
  { value: "fast_food", label: "Fast food" },
  { value: "bakery", label: "Panadería" },
  { value: "other", label: "Otro" },
];
const CUISINE_TYPES = [
  "mediterranean", "spanish", "italian", "japanese", "mexican", "chinese",
  "indian", "french", "american", "fusion", "seafood", "vegetarian", "other",
];
const PRICE_RANGES = ["€", "€€", "€€€", "€€€€"];

const EMPTY = {
  name: "", business_type: "restaurant", cuisine_type: "",
  price_range: "€€", target_audience: "", address: "", city: "",
  country: "", phone: "", email: "", description: "",
};

export default function RestaurantEditDialog({ open, onOpenChange, restaurant, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isOwner = (restaurant?.role || "owner") === "owner";
  const isManager = restaurant?.role === "manager";
  const readOnly = !isOwner && !isManager;

  useEffect(() => {
    if (!open || !restaurant) return;
    setForm({
      name: restaurant.name || "",
      business_type: restaurant.business_type || "restaurant",
      cuisine_type: restaurant.cuisine_type || "",
      price_range: restaurant.price_range || "€€",
      target_audience: restaurant.target_audience || "",
      address: restaurant.address || "",
      city: restaurant.city || "",
      country: restaurant.country || "",
      phone: restaurant.phone || "",
      email: restaurant.email || "",
      description: restaurant.description || "",
    });
  }, [open, restaurant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSaving(true);
    try {
      // Normalize empty strings → null so the backend doesn't overwrite with ""
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === "") payload[k] = null;
      });
      // name + business_type are required so never null
      payload.name = form.name.trim();
      payload.business_type = form.business_type || "restaurant";
      await api.updateRestaurant(restaurant.restaurant_id, payload);
      toast.success("Restaurante actualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar {restaurant?.name || "restaurante"}</DialogTitle>
          <DialogDescription>
            {readOnly
              ? "Solo el dueño o encargado pueden editar estos datos."
              : "Actualiza los datos visibles en la plataforma y el sitio web."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre *</Label>
            <Input value={form.name} disabled={readOnly}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="edit-name-input" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de negocio</Label>
              <Select disabled={readOnly} value={form.business_type}
                onValueChange={(v) => setForm(f => ({ ...f, business_type: v }))}>
                <SelectTrigger data-testid="edit-business-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de cocina</Label>
              <Select disabled={readOnly} value={form.cuisine_type || "other"}
                onValueChange={(v) => setForm(f => ({ ...f, cuisine_type: v }))}>
                <SelectTrigger data-testid="edit-cuisine-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CUISINE_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rango de precios</Label>
              <Select disabled={readOnly} value={form.price_range}
                onValueChange={(v) => setForm(f => ({ ...f, price_range: v }))}>
                <SelectTrigger data-testid="edit-price-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICE_RANGES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Público objetivo</Label>
              <Input disabled={readOnly} value={form.target_audience}
                onChange={(e) => setForm(f => ({ ...f, target_audience: e.target.value }))}
                data-testid="edit-target-audience"
                placeholder="Ej: familias, turistas, oficinas…" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Dirección</Label>
            <Input disabled={readOnly} value={form.address}
              onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
              data-testid="edit-address"
              placeholder="Calle, número" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Ciudad</Label>
              <Input disabled={readOnly} value={form.city}
                data-testid="edit-city"
                onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>País</Label>
              <Input disabled={readOnly} value={form.country}
                data-testid="edit-country"
                onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input disabled={readOnly} value={form.phone}
                data-testid="edit-phone"
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input disabled={readOnly} type="email" value={form.email}
                data-testid="edit-email"
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea disabled={readOnly} rows={3} value={form.description}
              data-testid="edit-description"
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            {!readOnly && (
              <Button type="submit" disabled={saving} data-testid="save-restaurant-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
